import { supabase } from '../lib/supabase';

export const departmentalBudgetService = {
  async getAllBudgets(filters = {}) {
    let query = supabase
      ?.from('departmental_budgets')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    if (filters?.department) {
      query = query?.eq('department', filters?.department);
    }

    if (filters?.fiscalYear) {
      query = query?.eq('fiscal_year', filters?.fiscalYear);
    }

    if (filters?.status) {
      query = query?.eq('budget_status', filters?.status);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(budget => ({
      id: budget?.id,
      companyId: budget?.company_id,
      department: budget?.department,
      fiscalYear: budget?.fiscal_year,
      fiscalPeriod: budget?.fiscal_period,
      allocatedBudgetKina: parseFloat(budget?.allocated_budget_kina) || 0,
      spentAmountKina: parseFloat(budget?.spent_amount_kina) || 0,
      remainingBalanceKina: parseFloat(budget?.remaining_balance_kina) || 0,
      payrollCostKina: parseFloat(budget?.payroll_cost_kina) || 0,
      complianceCostKina: parseFloat(budget?.compliance_cost_kina) || 0,
      operationalCostKina: parseFloat(budget?.operational_cost_kina) || 0,
      variancePercentage: parseFloat(budget?.variance_percentage) || 0,
      budgetStatus: budget?.budget_status,
      approvedBy: budget?.approved_by,
      approvedAt: budget?.approved_at,
      createdBy: budget?.created_by,
      createdAt: budget?.created_at,
      updatedAt: budget?.updated_at
    })) || [];
  },

  async getBudgetById(budgetId) {
    const { data, error } = await supabase
      ?.from('departmental_budgets')
      ?.select('*')
      ?.eq('id', budgetId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      department: data?.department,
      fiscalYear: data?.fiscal_year,
      fiscalPeriod: data?.fiscal_period,
      allocatedBudgetKina: parseFloat(data?.allocated_budget_kina) || 0,
      spentAmountKina: parseFloat(data?.spent_amount_kina) || 0,
      remainingBalanceKina: parseFloat(data?.remaining_balance_kina) || 0,
      payrollCostKina: parseFloat(data?.payroll_cost_kina) || 0,
      complianceCostKina: parseFloat(data?.compliance_cost_kina) || 0,
      operationalCostKina: parseFloat(data?.operational_cost_kina) || 0,
      variancePercentage: parseFloat(data?.variance_percentage) || 0,
      budgetStatus: data?.budget_status,
      approvedBy: data?.approved_by,
      approvedAt: data?.approved_at,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async getPayrollBreakdown(budgetId) {
    const { data, error } = await supabase
      ?.from('payroll_cost_breakdown')
      ?.select('*')
      ?.eq('budget_id', budgetId)
      ?.order('period_start', { ascending: false });

    if (error) throw error;

    return data?.map(breakdown => ({
      id: breakdown?.id,
      budgetId: breakdown?.budget_id,
      department: breakdown?.department,
      employeeCount: breakdown?.employee_count || 0,
      totalGrossKina: parseFloat(breakdown?.total_gross_kina) || 0,
      totalNetKina: parseFloat(breakdown?.total_net_kina) || 0,
      totalSwtKina: parseFloat(breakdown?.total_swt_kina) || 0,
      totalNasfundEmployeeKina: parseFloat(breakdown?.total_nasfund_employee_kina) || 0,
      totalNasfundEmployerKina: parseFloat(breakdown?.total_nasfund_employer_kina) || 0,
      totalAllowancesKina: parseFloat(breakdown?.total_allowances_kina) || 0,
      totalDeductionsKina: parseFloat(breakdown?.total_deductions_kina) || 0,
      periodStart: breakdown?.period_start,
      periodEnd: breakdown?.period_end,
      createdAt: breakdown?.created_at
    })) || [];
  },

  async getForecasts(budgetId) {
    const { data, error } = await supabase
      ?.from('budget_forecasts')
      ?.select('*')
      ?.eq('budget_id', budgetId)
      ?.order('forecast_year', { ascending: true });

    if (error) throw error;

    return data?.map(forecast => ({
      id: forecast?.id,
      budgetId: forecast?.budget_id,
      department: forecast?.department,
      forecastPeriod: forecast?.forecast_period,
      forecastYear: forecast?.forecast_year,
      forecastQuarter: forecast?.forecast_quarter,
      projectedBudgetKina: parseFloat(forecast?.projected_budget_kina) || 0,
      projectedPayrollKina: parseFloat(forecast?.projected_payroll_kina) || 0,
      projectedComplianceKina: parseFloat(forecast?.projected_compliance_kina) || 0,
      projectedOperationalKina: parseFloat(forecast?.projected_operational_kina) || 0,
      headcountProjection: forecast?.headcount_projection || 0,
      confidenceScore: parseFloat(forecast?.confidence_score) || 0,
      basedOnHistoricalData: forecast?.based_on_historical_data,
      assumptions: forecast?.assumptions,
      createdBy: forecast?.created_by,
      createdAt: forecast?.created_at
    })) || [];
  },

  async getComplianceCostImpact(budgetId) {
    const { data, error } = await supabase
      ?.from('compliance_cost_impact')
      ?.select('*')
      ?.eq('budget_id', budgetId)
      ?.order('implementation_date', { ascending: false });

    if (error) throw error;

    return data?.map(impact => ({
      id: impact?.id,
      budgetId: impact?.budget_id,
      department: impact?.department,
      complianceCategory: impact?.compliance_category,
      regulationReference: impact?.regulation_reference,
      estimatedCostKina: parseFloat(impact?.estimated_cost_kina) || 0,
      actualCostKina: parseFloat(impact?.actual_cost_kina) || 0,
      costVarianceKina: parseFloat(impact?.cost_variance_kina) || 0,
      impactDescription: impact?.impact_description,
      affectedEmployees: impact?.affected_employees || 0,
      implementationDate: impact?.implementation_date,
      notes: impact?.notes,
      createdAt: impact?.created_at,
      updatedAt: impact?.updated_at
    })) || [];
  },

  async getSpendingTrends(budgetId) {
    const { data, error } = await supabase
      ?.from('budget_spending_trends')
      ?.select('*')
      ?.eq('budget_id', budgetId)
      ?.order('trend_month', { ascending: true });

    if (error) throw error;

    return data?.map(trend => ({
      id: trend?.id,
      budgetId: trend?.budget_id,
      department: trend?.department,
      trendMonth: trend?.trend_month,
      monthlySpentKina: parseFloat(trend?.monthly_spent_kina) || 0,
      cumulativeSpentKina: parseFloat(trend?.cumulative_spent_kina) || 0,
      budgetUtilizationPercentage: parseFloat(trend?.budget_utilization_percentage) || 0,
      payrollPercentage: parseFloat(trend?.payroll_percentage) || 0,
      compliancePercentage: parseFloat(trend?.compliance_percentage) || 0,
      operationalPercentage: parseFloat(trend?.operational_percentage) || 0,
      createdAt: trend?.created_at
    })) || [];
  },

  async getBudgetAlerts(budgetId) {
    const { data, error } = await supabase
      ?.from('budget_alerts')
      ?.select('*')
      ?.eq('budget_id', budgetId)
      ?.eq('is_read', false)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(alert => ({
      id: alert?.id,
      budgetId: alert?.budget_id,
      department: alert?.department,
      alertType: alert?.alert_type,
      severity: alert?.severity,
      title: alert?.title,
      message: alert?.message,
      thresholdPercentage: parseFloat(alert?.threshold_percentage) || null,
      currentPercentage: parseFloat(alert?.current_percentage) || null,
      amountKina: parseFloat(alert?.amount_kina) || null,
      isRead: alert?.is_read,
      readAt: alert?.read_at,
      createdAt: alert?.created_at
    })) || [];
  },

  async getBudgetOverview() {
    const budgets = await this.getAllBudgets();

    const totalAllocated = budgets?.reduce((sum, b) => sum + b?.allocatedBudgetKina, 0);
    const totalSpent = budgets?.reduce((sum, b) => sum + b?.spentAmountKina, 0);
    const totalRemaining = budgets?.reduce((sum, b) => sum + b?.remainingBalanceKina, 0);
    const totalPayroll = budgets?.reduce((sum, b) => sum + b?.payrollCostKina, 0);
    const totalCompliance = budgets?.reduce((sum, b) => sum + b?.complianceCostKina, 0);

    const overBudgetCount = budgets?.filter(b => b?.variancePercentage > 0)?.length || 0;
    const underBudgetCount = budgets?.filter(b => b?.variancePercentage < 0)?.length || 0;
    const onTrackCount = budgets?.filter(b => b?.variancePercentage === 0)?.length || 0;

    return {
      totalAllocated,
      totalSpent,
      totalRemaining,
      totalPayroll,
      totalCompliance,
      utilizationPercentage: totalAllocated > 0 ? (totalSpent / totalAllocated) * 100 : 0,
      overBudgetCount,
      underBudgetCount,
      onTrackCount,
      totalDepartments: budgets?.length || 0
    };
  },

  formatKina(amount) {
    if (amount === null || amount === undefined) return 'K 0.00';
    return `K ${parseFloat(amount)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  },

  formatPercentage(value) {
    if (value === null || value === undefined) return '0.00%';
    return `${parseFloat(value)?.toFixed(2)}%`;
  }
};

export default departmentalBudgetService;